import { FiltersType, ListingRequest } from '@/api/types';
import { client } from '@/client';
import { Branch } from '@/client/branches';
import { MessageResponseModel } from '@/client/models';
import {
  BulkUserActionResult,
  DeleteUserRequest,
  DeleteUsersBulkRequest,
  ListRoleResponse,
  ListUserCampaignsResponse,
  ListUserCoursesResponse,
  ListUsersResponse,
  PermanentlyRemoveUserRequest,
  PermanentlyRemoveUsersBulkRequest,
  RestoreUserRequest,
  RestoreUsersBulkRequest,
  SaveUserRequest,
  UpdateUserRequest,
  User,
} from '@/client/users';
import {
  DeleteUserMutation,
  GenericForbiddenApiError,
  ResendWelcomeEmailMutation,
  SaveUserMutation,
} from '@/hooks/query';
import { partialRequests } from '@/utils/helpers';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const queryKey = 'users';
const singleQueryKey = 'user';

export const useUsers = (
  params: UseQueryOptions & {
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
    withBranches?: boolean;
    withGroups?: number;
  },
): UseQueryResult<ListUsersResponse> & {
  users: ListUsersResponse | undefined;
} => {
  const {
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [queryKey, take, skip, JSON.stringify(sort), JSON.stringify(filters)],
    () => client.users.getUsers(params),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    users: data,
    ...(rest as UseQueryResult<ListUsersResponse>),
  };
};

export const useAccountUsers = (
  params: UseQueryOptions & {
    accountId?: string;
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
    withBranches?: boolean;
    withGroups?: number;
    withImpersonationEligibility?: number;
    sortNulls?: 'first' | 'last';
  },
): UseQueryResult<ListUsersResponse> & {
  users: ListUsersResponse | undefined;
} => {
  const {
    accountId,
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    sortNulls,
    withImpersonationEligibility,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [
      queryKey,
      accountId,
      take,
      skip,
      JSON.stringify(sort),
      JSON.stringify(filters),
      sortNulls,
      withImpersonationEligibility,
    ],
    () => client.users.getAccountUsers(params, accountId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      keepPreviousData: true,
      ...(restOptions as any),
    },
  );

  return {
    users: data,
    ...(rest as UseQueryResult<ListUsersResponse>),
  };
};

export const useUsersPartialRequest = (
  params: UseQueryOptions & {
    sort?: string[];
    filters?: FiltersType;
    withBranches?: boolean;
    withGroups?: number;
  },
): UseQueryResult<User[]> & {
  users: User[] | undefined;
} => {
  const {
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;
  const usersRequest = async (params: ListingRequest) =>
    await client.users.getUsers(params);
  const { data, ...rest } = useQuery(
    [`${queryKey}-partial`, JSON.stringify(sort), JSON.stringify(filters)],
    () => partialRequests(filters || [], usersRequest, params),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    users: data,
    ...(rest as UseQueryResult<User[]>),
  };
};

export const useAccountUsersPartialRequest = (
  params: UseQueryOptions & {
    accountId?: string;
    sort?: string[];
    filters?: FiltersType;
    withBranches?: boolean;
    withGroups?: number;
  },
): UseQueryResult<User[]> & {
  users: User[] | undefined;
} => {
  const {
    accountId,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;
  const usersRequest = async (params: ListingRequest) =>
    await client.users.getAccountUsers(params, accountId);
  const { data, ...rest } = useQuery(
    [`${queryKey}-partial`, JSON.stringify(sort), JSON.stringify(filters)],
    () => partialRequests(filters || [], usersRequest, params),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    users: data,
    ...(rest as UseQueryResult<User[]>),
  };
};

export const useUser = (
  params: UseQueryOptions & {
    userId?: string;
    accountId?: string;
  },
): UseQueryResult<User> & {
  user: User | undefined;
} => {
  const {
    accountId,
    userId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [singleQueryKey, userId],
    () => (userId ? client.users.getUser(userId, accountId) : undefined),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<Branch>),
    },
  );

  return {
    user: data,
    ...(rest as UseQueryResult<User>),
  };
};

export const useSaveUser = (): {
  create: (payload: SaveUserRequest) => Promise<User>;
} & SaveUserMutation => {
  const { mutateAsync, ...rest } = useMutation<
    User,
    GenericForbiddenApiError,
    SaveUserRequest
  >('addUser', (payload: SaveUserRequest) => client.users.saveUser(payload));

  return { create: mutateAsync, ...(rest as any) };
};

export const useUpdateUser = (): {
  update: (payload: UpdateUserRequest) => Promise<User>;
} & SaveUserMutation => {
  const { mutateAsync, ...rest } = useMutation<
    User,
    GenericForbiddenApiError,
    UpdateUserRequest
  >('updateUser', (payload: UpdateUserRequest) =>
    client.users.updateUser(payload),
  );

  return { update: mutateAsync, ...(rest as any) };
};

export const useResendWelcomeEmail = (): {
  resend: (payload: {
    userId: string;
    accountId: string;
  }) => Promise<MessageResponseModel>;
} & ResendWelcomeEmailMutation => {
  const mutation = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    { userId: string; accountId: string }
  >('resendWelcomeEmail', (payload) =>
    client.users.resendWelcomeEmail(payload.userId, payload.accountId),
  );

  return { ...mutation, resend: mutation.mutateAsync };
};

export const useDeleteUser = (): {
  delete: (payload: DeleteUserRequest) => Promise<MessageResponseModel>;
} & DeleteUserMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    DeleteUserRequest
  >('deleteUser', (payload: DeleteUserRequest) =>
    client.users.deleteUser(payload.userId, payload.accountId),
  );

  return { delete: mutateAsync, ...(rest as any) };
};

export const useRestoreUser = (): {
  restore: (payload: RestoreUserRequest) => Promise<MessageResponseModel>;
} & DeleteUserMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    RestoreUserRequest
  >('restoreUser', (payload: RestoreUserRequest) =>
    client.users.restoreUser(payload.userId, payload.accountId),
  );

  return { restore: mutateAsync, ...(rest as any) };
};

export const usePermanentlyRemoveUser = (): {
  permanentlyRemove: (
    payload: PermanentlyRemoveUserRequest,
  ) => Promise<MessageResponseModel>;
} & DeleteUserMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    PermanentlyRemoveUserRequest
  >('permanentlyRemoveUser', (payload: PermanentlyRemoveUserRequest) =>
    client.users.permanentlyRemoveUser(payload.userId, payload.accountId),
  );

  return { permanentlyRemove: mutateAsync, ...(rest as any) };
};

export const useDeleteUsersBulk = (): {
  deleteBulk: (
    payload: DeleteUsersBulkRequest,
  ) => Promise<MessageResponseModel>;
} & DeleteUserMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    DeleteUsersBulkRequest
  >('deleteUsersBulk', (payload: DeleteUsersBulkRequest) =>
    client.users.deleteUsers(payload.userIds, payload.accountId),
  );

  return { deleteBulk: mutateAsync, ...(rest as any) };
};

export const useRestoreUsersBulk = (): {
  restoreBulk: (
    payload: RestoreUsersBulkRequest,
  ) => Promise<BulkUserActionResult>;
} & UseMutationResult<
  BulkUserActionResult,
  GenericForbiddenApiError,
  RestoreUsersBulkRequest
> => {
  const { mutateAsync, ...rest } = useMutation<
    BulkUserActionResult,
    GenericForbiddenApiError,
    RestoreUsersBulkRequest
  >('restoreUsersBulk', (payload: RestoreUsersBulkRequest) =>
    client.users.restoreUsers(payload.userIds, payload.accountId),
  );

  return { restoreBulk: mutateAsync, ...(rest as any) };
};

export const usePermanentlyRemoveUsersBulk = (): {
  permanentlyRemoveBulk: (
    payload: PermanentlyRemoveUsersBulkRequest,
  ) => Promise<BulkUserActionResult>;
} & UseMutationResult<
  BulkUserActionResult,
  GenericForbiddenApiError,
  PermanentlyRemoveUsersBulkRequest
> => {
  const { mutateAsync, ...rest } = useMutation<
    BulkUserActionResult,
    GenericForbiddenApiError,
    PermanentlyRemoveUsersBulkRequest
  >(
    'permanentlyRemoveUsersBulk',
    (payload: PermanentlyRemoveUsersBulkRequest) =>
      client.users.permanentlyRemoveUsers(payload.userIds, payload.accountId),
  );

  return { permanentlyRemoveBulk: mutateAsync, ...(rest as any) };
};

export const useRoles = (
  params: UseQueryOptions & {
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
    withBranches?: boolean;
  },
): UseQueryResult<ListRoleResponse> & {
  roles: ListRoleResponse | undefined;
} => {
  const {
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [queryKey, take, skip, JSON.stringify(sort), JSON.stringify(filters)],
    () => client.roles.getRoles(params),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    roles: data,
    ...(rest as UseQueryResult<ListRoleResponse>),
  };
};

export const useUserCourses = (
  params: UseQueryOptions & {
    accountId?: string;
    userId?: string;
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  },
): UseQueryResult<ListUserCoursesResponse> & {
  userCourses: ListUserCoursesResponse | undefined;
} => {
  const {
    accountId,
    userId,
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;
  const { data, ...rest } = useQuery(
    [
      `${queryKey}UserCourses`,
      take,
      skip,
      JSON.stringify(sort),
      JSON.stringify(filters),
    ],
    () => client.users.getUserCourses(params, accountId, userId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    userCourses: data,
    ...(rest as UseQueryResult<ListUserCoursesResponse>),
  };
};

export const useUserCampaigns = (
  params: UseQueryOptions & {
    userId?: string;
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  },
): UseQueryResult<ListUserCampaignsResponse> & {
  userCampaigns: ListUserCampaignsResponse | undefined;
} => {
  const {
    userId,
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;
  const { data, ...rest } = useQuery(
    [
      `${queryKey}UserCampaigns`,
      take,
      skip,
      JSON.stringify(sort),
      JSON.stringify(filters),
    ],
    () => client.users.getUserCampaigns(params, userId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    userCampaigns: data,
    ...(rest as UseQueryResult<ListUserCampaignsResponse>),
  };
};

export const useApprovePendingUser = () => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    { userId: string; accountId: string; roleId: string }
  >('approvePendingUser', (payload) =>
    client.users.approvePendingUser(
      payload.userId,
      payload.accountId,
      payload.roleId,
    ),
  );

  return { approve: mutateAsync, ...rest };
};

export const useDenyPendingUser = () => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    { userId: string; accountId: string }
  >('denyPendingUser', (payload) =>
    client.users.denyPendingUser(payload.userId, payload.accountId),
  );

  return { deny: mutateAsync, ...rest };
};

export const useDismissAddUsersBanner = () => {
  const { mutateAsync, ...rest } = useMutation<
    User,
    GenericForbiddenApiError,
    void
  >('dismissAddUsersBanner', () => client.default.dismissAddUsersBanner());

  return { dismiss: mutateAsync, ...rest };
};
